import styles from "@/styles/Communication.module.scss";
import Table from "@/components/table/Table";
import { useBuildingAdminOrReceptionistAccess } from "@/hooks/useReceptionistAccess";
import {
  DATE_DAY_MONTH_LOCALE,
  firebaseTimestampToDate,
  getPercentage,
  percentageSortingFn,
  timestampMultiColumnSortingFn,
  timestampSortingFn,
} from "@/lib";
import { ArticleStatus, ExtendedArticle, NestedKeys } from "@/types";
import {
  Button,
  Chip,
  Icon,
  InputField,
  LoadingSpinner,
} from "@app-components";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { allArticlesOptions } from "@/api/functions/communication";
import { useBuilding } from "@/api";
import { useWelcomeCommunicationAccess } from "@/hooks/useWelcomeCommunicationAccess";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import ArticlesTableActions from "@/components/communication/ArticlesTableActions";
import { Tooltip } from "react-tooltip";
import { useRouterStore } from "@/store/zustandStore";
import { useBuildingQuery } from "@/components/RouterSetup";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNestedValue(article: any, path: string[]): any {
  return path.reduce(
    (acc, key) => (acc != null ? acc[key] : undefined),
    article,
  );
}
const statusSortMap: { [key in ArticleStatus]: number } = {
  [ArticleStatus.DRAFT]: 0,
  [ArticleStatus.PLANNED]: 1,
  [ArticleStatus.PUBLISHED]: 2,
  [ArticleStatus.ARCHIVED]: 3,
};
type SearchableColumns = NestedKeys<ExtendedArticle>;

const columnHelper = createColumnHelper<ExtendedArticle>();

export const Route = createFileRoute("/_portal/building/communication/")({
  component: RouteComponent,
  loader: () => {
    const { queryClient } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(allArticlesOptions);
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { data: building } = useBuilding();
  const { hasCommunication } = useWelcomeCommunicationAccess();

  if (!hasCommunication) return null;

  return (
    <div>
      <div className="pageHeader">
        <h1>{t("COMMUNICATION.HEADER")}</h1>
        <p>
          {t("COMMUNICATION.SUBHEADER", {
            buildingName:
              building?.displayName ?? t("THE_BUILDING").toLowerCase(),
          })}
        </p>
      </div>
      <Suspense fallback={<LoadingSpinner />}>
        <Content />
      </Suspense>
    </div>
  );
}

function Content() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const searchableColumns: Array<SearchableColumns> = [
    "author.name",
    `title.${i18n.language === "en" ? "en" : "no"}`,
  ];
  const { allowBuildingAdminOrReceptionistAccess } =
    useBuildingAdminOrReceptionistAccess();
  const { hasCommunication } = useWelcomeCommunicationAccess();
  const { isLoading: isLoadingAllArticles, data: allArticles } =
    useBuildingQuery<ExtendedArticle[]>(allArticlesOptions);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredArticles, setFilteredArticles] = useState<ExtendedArticle[]>(
    [],
  );

  useEffect(() => {
    if (searchTerm && !!allArticles?.length) {
      const searchTerms = searchTerm.toLowerCase().split(" ");
      setFilteredArticles(
        allArticles.filter((article: ExtendedArticle) =>
          searchTerms.every((term) =>
            searchableColumns.some((column) => {
              const value = getNestedValue(article, column.split("."));
              return (value?.toString().toLowerCase() ?? "").includes(term);
            }),
          ),
        ),
      );
    } else {
      setFilteredArticles(allArticles ?? []);
    }
  }, [searchTerm, allArticles]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        cell: (info) => info.getValue(),
        header: () => <div>ID</div>,
        meta: {
          label: "ID",
        },
      }),
      columnHelper.accessor("disregardConsent", {
        cell: (info) => (
          <Chip
            className="tableTag"
            label={
              info.getValue()
                ? t("NEWS.CATEGORIES.OPERATIONAL")
                : t("NEWS.CATEGORIES.DEFAULT")
            }
          />
        ),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMMUNICATION.CATEGORY")}
          />
        ),
        meta: {
          label: t("COMMUNICATION.CATEGORY"),
        },
      }),
      columnHelper.accessor((row) => [row.publishedAt, row.plannedPublishOn], {
        id: "publishedAt",
        cell: (info) => {
          const [publishedAt, plannedPublishAt] = info.getValue();
          const timestampToShow = publishedAt || plannedPublishAt;
          if (!timestampToShow) return "";
          const date = firebaseTimestampToDate(timestampToShow);

          return (
            <>
              {!publishedAt && plannedPublishAt ? (
                <Icon
                  name="schedule"
                  className="planned-publish-icon"
                  style={{
                    verticalAlign: "text-top",
                    color: "var(--wlcm-color-success-green)",
                    marginRight: "var(--wlcm-spacing-xs)",
                  }}
                />
              ) : (
                <></>
              )}
              {`${DATE_DAY_MONTH_LOCALE(i18n.language).format(date)}`}
            </>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMMUNICATION.PUBLISHED_AT")}
          />
        ),
        sortUndefined: -1,
        sortingFn: timestampMultiColumnSortingFn([
          "publishedAt",
          "plannedPublishOn",
        ]),
        meta: {
          label: t("COMMUNICATION.PUBLISHED_AT"),
        },
      }),
      columnHelper.accessor(
        (row) => [row.archivedAt, row.plannedArchivationOn],
        {
          id: "archivedAt",
          cell: (info) => {
            const [archivedAt, plannedArchivationOn] = info.getValue();
            const timestampToShow = archivedAt || plannedArchivationOn;
            if (!timestampToShow) return "";
            const date = firebaseTimestampToDate(timestampToShow);

            return (
              <>
                {!archivedAt && plannedArchivationOn ? (
                  <Icon
                    name="schedule"
                    className="planned-publish-icon"
                    style={{
                      verticalAlign: "text-top",
                      color: "var(--wlcm-color-warning-orange)",
                      marginRight: "var(--wlcm-spacing-xs)",
                    }}
                  />
                ) : (
                  <></>
                )}
                {`${DATE_DAY_MONTH_LOCALE(i18n.language).format(date)}`}
              </>
            );
          },
          header: (info) => (
            <SortableTableHeader
              info={info}
              label={t("COMMUNICATION.ARCHIVED_AT")}
            />
          ),
          sortUndefined: -1,
          sortingFn: timestampMultiColumnSortingFn([
            "archivedAt",
            "plannedArchivationOn",
          ]),
          meta: {
            label: t("COMMUNICATION.ARCHIVED_AT"),
          },
        },
      ),
      columnHelper.accessor("lastUpdated.at", {
        id: "updatedAt",
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "";
          const date = firebaseTimestampToDate(value);

          return `${DATE_DAY_MONTH_LOCALE(i18n.language).format(date)}`;
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMMUNICATION.UPDATED_AT")}
          />
        ),
        sortUndefined: -1,
        sortingFn: timestampSortingFn,
        meta: {
          label: t("COMMUNICATION.UPDATED_AT"),
        },
      }),
      columnHelper.accessor("author.name", {
        id: "authorName",
        cell: (info) => info.getValue(),
        header: (info) => (
          <SortableTableHeader info={info} label={t("COMMUNICATION.AUTHOR")} />
        ),
        meta: {
          label: t("COMMUNICATION.AUTHOR"),
        },
      }),
      columnHelper.accessor("title", {
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className={styles.tableTruncate}>
              {i18n.language === "en" ? value.en : value.no}
            </span>
          );
        },
        header: (info) => (
          <SortableTableHeader info={info} label={t("COMMUNICATION.TITLE")} />
        ),
        sortingFn: (a, b) => {
          const lang = i18n.language === "en" ? "en" : "no";
          const aValue = a.original.title[lang];
          const bValue = b.original.title[lang];
          return aValue.localeCompare(bValue);
        },
        meta: {
          label: t("COMMUNICATION.TITLE"),
        },
      }),
      columnHelper.accessor(
        ({ recipients, statistics, status }) => ({
          recipients,
          statistics,
          status,
        }),
        {
          id: "readStatistics",
          cell: (info) => {
            const { recipients, statistics, status } = info.getValue();
            const totalReach = statistics?.reach?.recipients?.total ?? 0;
            const readCount = recipients.readLog?.length ?? 0;
            const percentage = getPercentage(readCount, totalReach);
            const color =
              status !== ArticleStatus.PUBLISHED
                ? "initial"
                : percentage < 10
                  ? "var(--wlcm-color-danger-red)"
                  : percentage < 50
                    ? "var(--wlcm-color-warning-orange)"
                    : "var(--wlcm-color-success-green)";
            return !totalReach ? (
              ""
            ) : (
              <>
                {readCount}
                {` / ${totalReach} `}
                <span
                  style={{
                    color,
                    paddingLeft: "var(--wlcm-spacing-xs)",
                  }}
                >{`(${percentage}%)`}</span>
              </>
            );
          },
          header: (info) => (
            <SortableTableHeader
              info={info}
              label={t("COMMUNICATION.READ_BY")}
            />
          ),
          sortingFn: percentageSortingFn(
            "recipients.readLog",
            "statistics.reach.recipients.total",
          ),
          meta: {
            label: t("COMMUNICATION.READ_BY"),
          },
        },
      ),
      columnHelper.accessor("status", {
        cell: (info) => (
          <span className={styles.status}>
            <div
              className={`${styles.statusDot} ${styles[info.getValue()]}`}
              aria-hidden
            />
            {t(`COMMUNICATION.STATUSES.${info.getValue()}`)}
          </span>
        ),
        header: (info) => (
          <SortableTableHeader info={info} label={t("COMMUNICATION.STATUS")} />
        ),
        sortingFn: (rowA, rowB, columnId) => {
          const a: ArticleStatus = rowA.getValue(columnId);
          const b: ArticleStatus = rowB.getValue(columnId);

          const aValue = statusSortMap[a];
          const bValue = statusSortMap[b];

          return aValue - bValue;
        },
        meta: {
          label: t("COMMUNICATION.STATUS"),
        },
      }),
      columnHelper.display({
        id: "actions",
        cell: ({ row }) => <ArticlesTableActions row={row} />,
        header: () => (
          <div className="sr-only">{t("BUILDING_VISITORS.ACTIONS")}</div>
        ),
      }),
    ],
    [],
  );

  if (!allowBuildingAdminOrReceptionistAccess || !hasCommunication) {
    return null;
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "var(--wlcm-spacing-sm)",
          flexWrap: "wrap-reverse",
        }}
      >
        <InputField
          label={t("ARTICLE.SEARCH_PLACEHOLDER")}
          placeholder={t("ARTICLE.SEARCH_PLACEHOLDER") + "..."}
          icon="search"
          value={searchTerm}
          compact
          hideLabel
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button
          icon="add"
          onMouseOver={() => {
            router.preloadRoute({ to: "/building/communication/new-article" });
          }}
          onClick={() =>
            navigate({ to: "/building/communication/new-article" })
          }
        >
          {t("COMMUNICATION.NEW_ARTICLE")}
        </Button>
      </div>
      <Table
        name="articles"
        data={filteredArticles}
        columns={columns}
        isLoading={isLoadingAllArticles}
        onRowHover={({ id }) => {
          router.preloadRoute({
            to: "/building/communication/$articleId",
            params: {
              articleId: id,
            },
          });
        }}
        onRowClick={({ id }) => {
          navigate({
            to: "/building/communication/$articleId",
            params: {
              articleId: id,
            },
          });
        }}
        defaultSorting={[
          {
            id: "updatedAt",
            desc: true,
          },
        ]}
        defaultColumnVisibility={{
          id: false,
          archivedAt: false,
        }}
        enableColumnControls
      />
      <Tooltip
        anchorSelect=".planned-publish-icon"
        content={t("ARTICLE.PLANNED_PUBLISH.SCHEDULED")}
      />
    </>
  );
}
